import MiniSearch, { type AsPlainObject, type SearchResult } from 'minisearch';

import { SEARCH_TIER } from '../../types';

import type {
    SearchDoc,
    SearchTier,
    SearchTierDurationsMs,
    SearchTierResultCounts,
} from '../../types';

const NAME_FIELD = 'name';
const NAME_SUFFIXES_FIELD = 'nameSuffixes';
const SKU_BARCODE_FIELD = 'skuBarcodeTokens';
const CATEGORY_ID_FIELD = 'categoryId';
const MIN_SUFFIX_LENGTH = 3;

const defaultExtractField = MiniSearch.getDefault('extractField') as (
    document: SearchDoc,
    fieldName: string,
) => unknown;

function buildSuffixes(name: string): string {
    const words = name.toLowerCase().split(/\s+/).filter(Boolean);
    const suffixes = new Set<string>();

    for (const word of words) {
        for (let start = 1; start <= word.length - MIN_SUFFIX_LENGTH; start++) {
            suffixes.add(word.slice(start));
        }
    }

    return Array.from(suffixes).join(' ');
}

function sanitizeAlphanumeric(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/gi, '');
}

function buildSkuBarcodeIndexValue(tokens: string): string {
    return tokens
        .split(/\s+/)
        .map(sanitizeAlphanumeric)
        .filter(Boolean)
        .join(' ');
}

const SEARCH_OPTIONS = {
    fields: [NAME_FIELD, NAME_SUFFIXES_FIELD, SKU_BARCODE_FIELD],
    storeFields: [CATEGORY_ID_FIELD],
    idField: 'id',
    extractField: (document: SearchDoc, fieldName: string) => {
        if (fieldName === NAME_SUFFIXES_FIELD) {
            return buildSuffixes(document.name);
        }

        if (fieldName === SKU_BARCODE_FIELD) {
            return buildSkuBarcodeIndexValue(document.skuBarcodeTokens ?? '');
        }

        return defaultExtractField(document, fieldName);
    },
    searchOptions: {
        fuzzy: 0.2,
        prefix: true,
    },
};

export function createSearchIndex(): MiniSearch<SearchDoc> {
    return new MiniSearch<SearchDoc>(SEARCH_OPTIONS);
}

export function deserializeSearchIndex(
    serialized: AsPlainObject,
): MiniSearch<SearchDoc> {
    return MiniSearch.loadJS<SearchDoc>(serialized, SEARCH_OPTIONS);
}

export function addDocs(index: MiniSearch<SearchDoc>, docs: SearchDoc[]): void {
    index.addAll(docs);
}

export function upsertDocs(
    index: MiniSearch<SearchDoc>,
    docs: SearchDoc[],
): void {
    for (const doc of docs) {
        if (index.has(doc.id)) {
            index.discard(doc.id);
        }
    }

    index.addAll(docs);
}

export function removeDocs(index: MiniSearch<SearchDoc>, ids: string[]): void {
    for (const id of ids) {
        if (index.has(id)) {
            index.discard(id);
        }
    }
}

function mergeRankedTiers(tiers: SearchResult[]): string[] {
    return [...new Set(tiers.map((tier) => tier.id))];
}

export interface QueryMetrics {
    ids: string[];
    tierDurationsMs: SearchTierDurationsMs;
    tierResultCounts: SearchTierResultCounts;
}

function emptyQueryMetrics(): QueryMetrics {
    const zeros: Record<SearchTier, number> = {
        [SEARCH_TIER.NAME]: 0,
        [SEARCH_TIER.SUFFIX]: 0,
        [SEARCH_TIER.SKU_BARCODE]: 0,
        [SEARCH_TIER.FUZZY]: 0,
    };

    return {
        ids: [],
        tierDurationsMs: { ...zeros },
        tierResultCounts: { ...zeros },
    };
}

function getSearchTiers(
    index: MiniSearch<SearchDoc>,
    term: string,
    filter: ((result: SearchResult) => boolean) | undefined,
): Array<{ tier: SearchTier; run: () => SearchResult[] }> {
    const skuBarcodeTerm = sanitizeAlphanumeric(term);

    return [
        {
            tier: SEARCH_TIER.NAME,
            run: () =>
                index.search(term, {
                    fields: [NAME_FIELD],
                    prefix: true,
                    fuzzy: false,
                    filter,
                }),
        },
        {
            tier: SEARCH_TIER.SUFFIX,
            run: () =>
                index.search(term, {
                    fields: [NAME_SUFFIXES_FIELD],
                    prefix: true,
                    fuzzy: false,
                    filter,
                }),
        },
        {
            tier: SEARCH_TIER.SKU_BARCODE,
            run: () =>
                skuBarcodeTerm
                    ? index.search(term, {
                          fields: [SKU_BARCODE_FIELD],
                          prefix: true,
                          fuzzy: false,
                          filter,
                          tokenize: () => [skuBarcodeTerm],
                      })
                    : [],
        },
        {
            tier: SEARCH_TIER.FUZZY,
            run: () =>
                index.search(term, {
                    fields: [NAME_FIELD, NAME_SUFFIXES_FIELD],
                    prefix: false,
                    fuzzy: 0.2,
                    filter,
                }),
        },
    ];
}

export function queryIndexWithMetrics(
    index: MiniSearch<SearchDoc>,
    term: string,
    categoryIds: string[] = [],
): QueryMetrics {
    if (!term.trim()) return emptyQueryMetrics();

    const filter = categoryIds.length
        ? (result: SearchResult) =>
              categoryIds.includes(result[CATEGORY_ID_FIELD] as string)
        : undefined;

    const allResults: SearchResult[] = [];
    const tierDurationsMs = {} as SearchTierDurationsMs;
    const tierResultCounts = {} as SearchTierResultCounts;

    for (const { tier, run } of getSearchTiers(index, term, filter)) {
        const startedAt = performance.now();
        const results = run();

        tierDurationsMs[tier] = performance.now() - startedAt;
        tierResultCounts[tier] = results.length;
        allResults.push(...results);
    }

    return {
        ids: mergeRankedTiers(allResults),
        tierDurationsMs,
        tierResultCounts,
    };
}

export function queryIndex(
    index: MiniSearch<SearchDoc>,
    term: string,
    categoryIds: string[] = [],
): string[] {
    return queryIndexWithMetrics(index, term, categoryIds).ids;
}

export function serializeSearchIndex(
    index: MiniSearch<SearchDoc>,
): AsPlainObject {
    return index.toJSON();
}
